import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield, BarChart3, Eye } from 'lucide-react';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(() => {
    const consent = localStorage.getItem('cookie-consent');
    return !consent;
  });
  const [showDetails, setShowDetails] = useState(false);

  const loadAnalytics = React.useCallback(() => {
    // Vérifier si Google Analytics n'est pas déjà chargé
    if (document.querySelector('script[src*="googletagmanager.com/gtag/js?id=G-ELFPZZTTM0"]')) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('✅ Google Analytics déjà chargé');
      }
      return;
    }

    // Load Google Analytics 4
    const gtag = document.createElement('script');
    gtag.src = 'https://www.googletagmanager.com/gtag/js?id=G-ELFPZZTTM0';
    gtag.async = true;
    document.head.appendChild(gtag);

    const gtagConfig = document.createElement('script');
    gtagConfig.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-ELFPZZTTM0', {
        anonymize_ip: true,
        cookie_expires: 63072000 // 2 ans maximum
      });
    `;
    document.head.appendChild(gtagConfig);

    // Analytics chargés avec consentement
    if (process.env.NODE_ENV === 'development') {
      console.debug('✅ Google Analytics chargé avec consentement');
    }
  }, []);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (consent === 'accepted') {
      // Load analytics only if consent was previously given
      loadAnalytics();
    }
  }, [loadAnalytics]);

  const removeAnalytics = () => {
    // Remove Google Analytics scripts
    const scripts = document.querySelectorAll('script[src*="googletagmanager"], script[src*="google-analytics"]');
    scripts.forEach(script => script.remove());
    
    // Clear Google Analytics cookies
    const cookies = document.cookie.split(';');
    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      if (name.startsWith('_ga') || name.startsWith('_gid') || name.startsWith('_gat')) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      }
    });

    // Clear dataLayer
    if (window.dataLayer) {
      window.dataLayer = [];
    }
    
    // Remplacer gtag par une fonction no-op au lieu de le supprimer
    // (gtag est une propriété non-configurable et ne peut pas être supprimée)
    if (window.gtag) {
      window.gtag = function() {
        // Fonction vide pour désactiver Google Analytics
      };
    }

    // Analytics supprimés - consentement refusé
    if (process.env.NODE_ENV === 'development') {
      console.debug('🚫 Analytics supprimés - consentement refusé');
    }
  };

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    loadAnalytics();
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookie-consent', 'declined');
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    removeAnalytics();
    setIsVisible(false);
  };

  const handleSettings = () => {
    setShowDetails(!showDetails);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-dark-surface border-t border-border shadow-2xl cookie-banner">
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="flex flex-col gap-4">
          {/* Header */}
          <div className="flex items-start space-x-3">
            <Cookie className="text-brand-secondary mt-1 flex-shrink-0" size={24} />
            <div className="flex-1">
              <h4 className="font-bold text-text-primary mb-1">
                🍪 Respect de votre vie privée
              </h4>
              <p className="text-text-secondary text-sm">
                Nous respectons votre vie privée. Choisissez quels cookies vous acceptez pour améliorer votre expérience sur notre site.
              </p>
            </div>
          </div>
          
          {/* Cookie Details */}
          {showDetails && (
            <div className="bg-dark-hover rounded-[10px] p-4 space-y-3 border border-border">
              <div className="flex items-start space-x-3">
                <Shield className="text-brand-secondary mt-1 flex-shrink-0" size={16} />
                <div>
                  <h5 className="font-semibold text-text-primary text-sm">Cookies essentiels</h5>
                  <p className="text-text-secondary text-xs">Nécessaires au fonctionnement du site (toujours actifs)</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <BarChart3 className="text-brand-secondary mt-1 flex-shrink-0" size={16} />
                <div>
                  <h5 className="font-semibold text-text-primary text-sm">Cookies analytiques (Google Analytics)</h5>
                  <p className="text-text-secondary text-xs">
                    Nous aident à comprendre comment vous utilisez notre site. 
                    Données anonymisées, stockées 26 mois maximum.
                  </p>
                </div>
              </div>
              
              <div className="text-xs text-text-muted bg-brand-secondary/10 p-2 rounded-[8px] border-l-2 border-brand-secondary">
                <Eye className="inline w-3 h-3 mr-1" />
                <strong>Transparence :</strong> En acceptant, vos données peuvent être transférées vers les États-Unis (Google LLC).
                Ces transferts sont encadrés par des garanties appropriées (ex. Data Privacy Framework lorsque applicable).
                Consultez notre <a href="/confidentialite" className="text-brand-secondary underline">Politique de Confidentialité</a> pour plus de détails.
              </div>
            </div>
          )}
          
          {/* Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              onClick={handleSettings}
              className="text-sm text-text-secondary underline hover:text-text-primary transition-colors order-2 sm:order-1"
            >
              {showDetails ? 'Masquer les détails' : 'Plus d\'informations'}
            </button>
            
            <div className="flex items-center space-x-3 order-1 sm:order-2 ml-auto">
              <button
                onClick={handleDecline}
                className="btn-secondary text-sm flex-1 sm:flex-none"
              >
                Refuser
              </button>
              <button
                onClick={handleAccept}
                className="btn-primary text-sm flex-1 sm:flex-none"
              >
                Accepter
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;